import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, InputLabel, FormControl, IconButton, Snackbar, Alert, Typography, Box
} from '@mui/material';
import { Edit, Delete, UploadFile, Download, Visibility } from '@mui/icons-material';
import { API_BASE_URL } from '../api';
import apiService from '../services/apiService';

const api = `${API_BASE_URL}drivers`;
const fileApi = `${API_BASE_URL}driver_files`;

const driverTypes = [
  { value: 'trainee', label: 'Trainee' },
  { value: 'junior', label: 'Junior' },
  { value: 'senior', label: 'Senior' },
];

export default function Drivers() {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentRoleName = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const u = JSON.parse(raw);
      return u?.role?.name || u?.role || null;
    } catch {
      return null;
    }
  })();
  const mayManageDrivers = currentRoleName && ['super_admin', 'admin', 'manager'].includes(currentRoleName);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', birth_date: '', experience_years: '', driver_type: 'junior', notes: '', senior_id: '', username: '', password: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fileDialog, setFileDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => { fetchDrivers(); }, []);

  async function fetchDrivers() {
    setLoading(true);
    try {
      const data = await apiService.get('/drivers');
      setDrivers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }

  function handleOpen(driver = null) {
    setEditDriver(driver);
    setForm(driver ? {
      name: driver.name || '',
      phone: driver.phone || '',
      email: driver.email || '',
      address: driver.address || '',
      birth_date: driver.birth_date || '',
      experience_years: driver.experience_years || '',
      driver_type: driver.driver_type || 'junior',
      notes: driver.notes || '',
      senior_id: driver.senior_id || '',
      username: driver.user?.email
        ? driver.user.email.endsWith('@workwise.com')
          ? driver.user.email.replace('@workwise.com', '')
          : driver.user.email
        : '',
      password: '', // Keep empty for editing - user can change if needed
    } : { name: '', phone: '', email: '', address: '', birth_date: '', experience_years: '', driver_type: 'junior', notes: '', senior_id: '', username: '', password: '' });
    setOpen(true);
  }
  function handleClose() { setOpen(false); setEditDriver(null); }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Prepare form data - only include password if it's provided
    const submitData = { ...form };
    if (editDriver && !submitData.password) {
      delete submitData.password; // Don't send empty password when editing
    }
    
    console.log('Submitting driver data:', submitData); // Debug log
    
    try {
      if (editDriver) {
        await apiService.put(`/drivers/${editDriver.id}`, submitData);
      } else {
        await apiService.post('/drivers', submitData);
      }
      
      setSnackbar({ open: true, message: 'Driver saved!', severity: 'success' });
      fetchDrivers();
      handleClose();
    } catch (error) {
      console.error('Error saving driver:', error);
      setSnackbar({ open: true, message: error.message || 'Error saving driver', severity: 'error' });
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this driver?')) return;
    
    try {
      await apiService.delete(`/drivers/${id}`);
      setSnackbar({ open: true, message: 'Driver deleted!', severity: 'success' });
      fetchDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      setSnackbar({ open: true, message: 'Error deleting driver', severity: 'error' });
    }
  }

  async function handleFileDialog(driver) {
    setSelectedDriver(driver);
    setFileDialog(true);
    
    try {
      const data = await apiService.get(`/driver_files?driver_id=${driver.id}`);
      setFiles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching files:', error);
      setFiles([]);
    }
  }
  function handleFileDialogClose() {
    setFileDialog(false);
    setSelectedDriver(null);
    setFiles([]);
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file || !selectedDriver) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('driver_id', selectedDriver.id);
    formData.append('file_type', 'document');
    
    try {
      await apiService.postFormData('/driver_files', formData);
      setSnackbar({ open: true, message: 'File uploaded!', severity: 'success' });
      
      // Refresh files list
      const data = await apiService.get(`/driver_files?driver_id=${selectedDriver.id}`);
      setFiles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error uploading file:', error);
      setSnackbar({ open: true, message: 'Error uploading file', severity: 'error' });
    } finally {
      setUploading(false);
    }
  }

  async function handleFileDelete(id) {
    if (!window.confirm('Delete this file?')) return;
    
    try {
      await apiService.delete(`/driver_files/${id}`);
      setSnackbar({ open: true, message: 'File deleted!', severity: 'success' });
      setFiles(files => files.filter(f => f.id !== id));
    } catch (error) {
      console.error('Error deleting file:', error);
      setSnackbar({ open: true, message: 'Error deleting file', severity: 'error' });
    }
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 3, fontSize: '2rem' }}>
        Drivers
      </Typography>
      {mayManageDrivers && (
        <Button 
          variant="contained" 
          startIcon={<Edit />} 
          onClick={() => handleOpen()} 
          sx={{ 
            mb: 3, 
            fontWeight: 600,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
            },
          }}
        >
          Add Driver
        </Button>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Login email</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Experience</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Senior</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Files</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drivers.map(driver => (
              <TableRow key={driver.id}>
                <TableCell>{driver.name}</TableCell>
                <TableCell>{driver.driver_type}</TableCell>
                <TableCell>{driver.phone}</TableCell>
                <TableCell>{driver.email}</TableCell>
                <TableCell sx={{ wordBreak: 'break-all' }}>{driver.user?.email || '—'}</TableCell>
                <TableCell>{driver.experience_years}</TableCell>
                <TableCell>{driver.senior?.name || '-'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleFileDialog(driver)}><UploadFile /></IconButton>
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => navigate(`/driver/${driver.id}`)}><Visibility /></IconButton>
                  {mayManageDrivers && (
                    <>
                      <IconButton onClick={() => handleOpen(driver)}><Edit /></IconButton>
                      <IconButton onClick={() => handleDelete(driver.id)}><Delete /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {drivers.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    No drivers found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.5rem' }}>
          {editDriver ? 'Edit Driver' : 'Add Driver'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField name="name" label="Name" value={form.name} onChange={handleChange} required fullWidth />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select name="driver_type" value={form.driver_type} label="Type" onChange={handleChange} required>
                {driverTypes.map(dt => <MenuItem key={dt.value} value={dt.value}>{dt.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              name="phone"
              label="Phone"
              value={form.phone}
              onChange={handleChange}
              fullWidth
              placeholder="+44 7700 900123"
            />
            <TextField
              name="email"
              label="Contact email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              helperText="If set, this address is used for driver portal login (must be unique). Otherwise use Username below."
            />
            <TextField name="address" label="Address" value={form.address} onChange={handleChange} fullWidth />
            <TextField name="birth_date" label="Birth Date" type="date" value={form.birth_date} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField name="experience_years" label="Experience (years)" type="number" value={form.experience_years} onChange={handleChange} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Senior (Manager)</InputLabel>
              <Select name="senior_id" value={form.senior_id} label="Senior (Manager)" onChange={handleChange}>
                <MenuItem value="">None</MenuItem>
                {drivers.filter(d => d.driver_type === 'senior' && (!editDriver || d.id !== editDriver.id)).map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField name="notes" label="Notes" value={form.notes} onChange={handleChange} fullWidth multiline minRows={2} />
            {editDriver ? (
              <>
                <TextField
                  name="username"
                  label="Login (username or email)"
                  value={form.username}
                  onChange={handleChange}
                  fullWidth
                  helperText="If contact email is empty: local part only becomes name@workwise.com; or enter a full email."
                />
                <TextField 
                  name="password" 
                  label="Password" 
                  type="password" 
                  value={form.password} 
                  onChange={handleChange} 
                  fullWidth 
                  placeholder="Leave empty to keep current password"
                  helperText="Leave empty to keep current password"
                />
              </>
            ) : (
              <>
                <TextField
                  name="username"
                  label="Username (for login)"
                  value={form.username}
                  onChange={handleChange}
                  fullWidth
                  required={!String(form.email || '').trim()}
                  helperText={
                    String(form.email || '').trim()
                      ? 'Optional if contact email is set — login uses contact email.'
                      : 'Required if no contact email — login will be username@workwise.com'
                  }
                />
                <TextField name="password" label="Password" type="password" value={form.password} onChange={handleChange} fullWidth required />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ fontWeight: 600 }}>
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={fileDialog} onClose={handleFileDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.5rem' }}>
          Driver Files
        </DialogTitle>
        <DialogContent>
          {mayManageDrivers && (
            <Box sx={{ mb: 2 }}>
              <Button 
                variant="contained" 
                component="label" 
                disabled={uploading}
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Upload File
                <input type="file" hidden ref={fileInputRef} onChange={handleFileUpload} />
              </Button>
            </Box>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>File Name</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Uploaded</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map(file => (
                <TableRow key={file.id}>
                  <TableCell>{file.file_name}</TableCell>
                  <TableCell>{file.file_type}</TableCell>
                  <TableCell>{file.uploaded_at?.slice(0, 10)}</TableCell>
                  <TableCell align="right">
                    <IconButton href={`${API_BASE_URL.replace('/api','')}/storage/${file.file_path}`} target="_blank"><Download /></IconButton>
                    {mayManageDrivers && (
                      <IconButton onClick={() => handleFileDelete(file.id)}><Delete /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {files.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                      No files.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFileDialogClose} sx={{ fontWeight: 500 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
} 